import { useEffect, useState } from "react";
import { Upload, Trash2, Loader2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fileUrl } from "@/lib/activity";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export const BannerLibrary = () => {
  const [banners, setBanners] = useState([]);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(null);

  const load = () => api.get("/banners").then((r) => setBanners(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pilih file gambar");
    setBusy(true);
    const fd = new FormData();
    fd.append("name", name);
    fd.append("file", file);
    try {
      await api.post("/banners", fd);
      toast.success("Banner ditambahkan");
      setName(""); setFile(null); e.target.reset?.();
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/banners/${del.id}`);
      toast.success("Banner dihapus");
      setDel(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="banner-library">
      <form onSubmit={upload} className="rounded-3xl bg-white border border-emerald-900/10 p-6 grid sm:grid-cols-3 gap-4 items-end">
        <div className="space-y-1.5"><Label htmlFor="banner-name">Nama Banner</Label><Input id="banner-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Contoh: Kajian Malam Jumat" data-testid="banner-name-input" /></div>
        <div className="space-y-1.5">
          <Label>File Gambar (JPG/PNG)</Label>
          <label className="flex h-10 items-center gap-2 rounded-md border border-input bg-white px-3 text-sm cursor-pointer hover:bg-emerald-50">
            <ImageIcon size={15} className="text-emerald-700 shrink-0" /><span className="truncate">{file ? file.name : "Pilih gambar..."}</span>
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="banner-file-input" />
          </label>
        </div>
        <Button type="submit" disabled={busy} className="rounded-full emerald-bg hover:bg-[#145c48]" data-testid="banner-upload-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <><Upload size={16} className="mr-1" /> Unggah Banner</>}</Button>
        <p className="sm:col-span-3 text-xs text-slate-500">Rekomendasi ukuran 1280×720 px atau 1920×1080 px (rasio 16:9), maks. 5 MB. Banner ditampilkan responsif dengan fokus di tengah gambar dan bisa dipakai di banyak kegiatan.</p>
      </form>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="banner-grid">
        {banners.map((b) => (
          <div key={b.id} className="group rounded-2xl overflow-hidden bg-white border border-emerald-900/10" data-testid={`banner-item-${b.id}`}>
            <div className="aspect-video overflow-hidden"><img src={fileUrl(b.path)} alt={b.name} className="h-full w-full object-cover object-center transition-transform group-hover:scale-105" /></div>
            <div className="p-3 flex items-center justify-between gap-2">
              <p className="text-sm font-medium truncate" data-testid={`banner-name-${b.id}`}>{b.name}</p>
              <button type="button" onClick={() => setDel(b)} className="text-slate-400 hover:text-red-600 shrink-0" data-testid={`banner-delete-${b.id}`}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {banners.length === 0 && <div className="col-span-full rounded-3xl border border-dashed border-emerald-900/20 p-10 text-center text-slate-400 text-sm" data-testid="banner-empty">Belum ada banner. Unggah banner pertama Anda.</div>}
      </div>
      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent data-testid="banner-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Hapus banner "{del?.name}"?</AlertDialogTitle><AlertDialogDescription>Kegiatan yang memakai banner ini akan kembali menggunakan banner otomatis sesuai kategori.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="banner-delete-cancel">Batal</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="banner-delete-confirm">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
